import { useMemo, useState } from 'react';
import CascaderCustom from '../../utils/CascaderCustom';
import { variableIsAnImage } from '../RightSide/variablesUtils';
import { message, Switch } from 'antd';
import Tooltip from '../common/Tooltip';
import { useStore } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { buildRecursiveFieldName, getSectionRecursiveMeta } from '../../utils/recursiveSections';
import logo from "../../images/symbol.svg";

const RecursiveFieldIcon = () => (
  <Tooltip zIndex={9999} title="Champ récursif (issu du modèle de données)">
    <FontAwesomeIcon
      icon={icon({ name: 'arrow-turn-down-right', style: 'regular' })}
      style={{ width: 20, height: 20, color: '#fa8c16' }}
    />
  </Tooltip>
);

const ApiVariableIcon = () => (
  <img src={logo} alt="custom" style={{ width: 20, height: 20 }} />
);

const buildVariableLabel = (text, isRecursive) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
    {isRecursive ? <RecursiveFieldIcon /> : <ApiVariableIcon />}
    {text}
  </div>
);

/**
 * Reconstruit la valeur `value` du Cascader (même forme que celle renvoyée par Ant Design au onChange),
 * à partir de `content.adminValue`, pour afficher le libellé quand `variablePath` est vide
 * (ex. passage en image variable sans resélection).
 */
const buildImageCascaderPathFromAdmin = (
  adminValue,
  currentDocument,
  variablesApi,
  /** Même référence que pour `formattingOpportunity` (obligatoire pour matcher les options) */
  variablesDataRest
) => {
  if (!adminValue || !currentDocument || !variablesApi) return null;
  const keys = adminValue.split('.');

  if (currentDocument.type === 'opportunity') {
    if (keys.length !== 2 || !variablesDataRest) return null;
    return [variablesDataRest, keys[0], keys[1]];
  }

  if (currentDocument.type === 'template') {
    if (keys.length !== 2) return null;
    return [keys[0], keys[1]];
  }

  if (currentDocument.type === 'offer') {
    if (keys.length === 2) {
      const [categoryKey, informationKey] = keys;
      const offerVarRoot = variablesApi.offer_variables;
      if (!offerVarRoot) return null;
      return ['offerVariables', [offerVarRoot, categoryKey, informationKey]];
    }
    if (keys.length === 3) {
      const [indexKey, categoryKey, informationKey] = keys;
      const oppBlock = variablesApi.opportunities_variables?.[indexKey];
      if (!oppBlock) return null;
      const originKey = Object.keys(oppBlock)[0];
      const oppWrapper = oppBlock[originKey];
      if (!oppWrapper) return null;
      return ['opportunityVariables', [oppWrapper, categoryKey, informationKey]];
    }
  }

  return null;
};

const selector = ({ defaultVariablesApi, currentDocument, currentTemplate, recursiveModelFields, sections }) => ({
  defaultVariablesApi,
  currentDocument,
  currentTemplate,
  recursiveModelFields,
  sections,
});
const ImageCascader = ({ onLoadImage, selectedBox }) => {
  const { t } = useTranslation();
  const {
    defaultVariablesApi,
    currentDocument,
    currentTemplate,
    recursiveModelFields,
    sections,
  } = useStore(selector);
  const [showOnlyRecursive, setShowOnlyRecursive] = useState(false);

  const recursiveModelName = useMemo(() => {
    const tplMeta = getSectionRecursiveMeta(currentTemplate);
    if (tplMeta.isRecursive && tplMeta.recursiveModelName) return tplMeta.recursiveModelName;
    if (selectedBox?.fromSectionId) {
      const section = sections?.find((s) => String(s.id) === String(selectedBox.fromSectionId));
      const meta = getSectionRecursiveMeta(section);
      if (meta.isRecursive) return meta.recursiveModelName;
    }
    return null;
  }, [currentTemplate, sections, selectedBox?.fromSectionId]);

  const isRecursiveTemplate = !!recursiveModelName;
  /** Une seule déstructuration par snapshot API : même objet que dans les `value` des options (opportunity). */
  const variablesDataRest = useMemo(() => {
    if (!defaultVariablesApi) return null;
    const { variable_groups, custom_text_variables, ...data } = defaultVariablesApi;
    return data;
  }, [defaultVariablesApi]);
  const [messageApi, contextHolder] = message.useMessage();
  const success = () => {
    messageApi.open({
      type: 'success',
      content: 'Image sélectionnée',
    });
  };

  const formatingData = (variablesApi, dataRestForOpportunity) => {
    if (!variablesApi) return;
    if (currentDocument.type === 'opportunity') {
      return formattingOpportunity(dataRestForOpportunity);
    }
    const {
      variable_groups: variableGroups,
      custom_text_variables: customTextVariables,
      ...data
    } = variablesApi;
    if (currentDocument.type === 'template') {
      let templateData;
      if (typeof data['offer_variables'] === 'undefined') {
        templateData = Object.keys(data)
          .map((group) => {
            return Object.values(data[group])
              .filter((variable) => variableIsAnImage(variable))
              .map((name) => {
                const labelText = t(`${group}.${name}`);
                return {
                  key: `${group}.${name}-variable`,
                  value: [group, name],
                  isRecursive: false,
                  label: buildVariableLabel(labelText, false),
                  name: labelText,
                };
              });
          })
          .flat();
      } else {
        templateData = Object.keys(data['offer_variables'])
          .map((group) => {
            return Object.values(data['offer_variables'][group])
              .filter((variable) => variableIsAnImage(variable))
              .map((name) => {
                const labelText = t(`${group}.${name}`);
                return {
                  key: `${group}.${name}-variable`,
                  value: [group, name],
                  isRecursive: false,
                  label: buildVariableLabel(labelText, false),
                  name: labelText,
                };
              });
          })
          .flat();
      }
      if (isRecursiveTemplate) {
        const modelName = recursiveModelName;
        const modelFields = recursiveModelFields?.[modelName] ?? [];
        const recursiveImageOptions = modelFields
          .filter((field) => field.type === 'image')
          .map((field) => {
            const labelText = buildRecursiveFieldName(modelName, field.label, t);
            return {
              key: `${modelName}.${field.key}-recursive`,
              value: [modelName, field.key],
              isRecursive: true,
              label: buildVariableLabel(labelText, true),
              name: labelText,
            };
          });
        templateData = [...recursiveImageOptions, ...(templateData ?? [])];
      }
      return templateData;
    }
    return [
        {
          key: 'offerVariables',
          value: 'offerVariables',
          label: "Variables de l'offre",
          children: offerVariables(data.offer_variables),
        },
        {
          key: 'opportunityVariables',
          value: 'opportunityVariables',
          label: 'Variables des opportunités',
          children: opportunitiesVariables(data.opportunities_variables),
        },
      ];
  };
  const offerVariables = (offerVariables) => {
    if (!offerVariables) return [];
    let offerData = Object.keys(offerVariables)
      .map((variableGroup) => {
        return Object.keys(offerVariables[variableGroup])
          .filter((variable) => variableIsAnImage(variable))
          .map((variableName) => {
            const labelText = t(`${variableGroup}.${variableName}`);
            return {
              key: `${variableGroup}.${variableName}-variable`,
              value: [offerVariables, variableGroup, variableName],
              isRecursive: false,
              label: buildVariableLabel(labelText, false),
              name: labelText,
            };
          });
      })
      .flat();
    return offerData;
  };
  const formattingOpportunity = (opportunity) => {
    if (!opportunity) return [];
    return Object.keys(opportunity)
      .map((variablegroup) => {
        return Object.keys(opportunity[variablegroup])
          .filter((variable) => variableIsAnImage(variable))
          .map((variableName) => {
            const labelText = t(`${variablegroup}.${variableName}`);
            return {
              key: `${variablegroup}.${variableName}`,
              value: [opportunity, variablegroup, variableName],
              isRecursive: false,
              label: buildVariableLabel(labelText, false),
              name: labelText,
            };
          });
      })
      .flat();
  };
  const opportunitiesVariables = (opportunities) => {
    if (!opportunities) return [];
    return opportunities.map((opportunity, idx) => {
      let opportunitydata = opportunity[Object.keys(opportunity).toString()];
      if (!opportunitydata) return [];
      return {
        key: Object.keys(opportunity).toString(),
        label: Object.keys(opportunity).toString(),
        value: Object.keys(opportunity).toString().replace(/ /g, '_'),
        children: Object.keys(opportunitydata)
          .map((variablegroup) => {
            return Object.keys(opportunitydata[variablegroup])
              .filter((variable) => variableIsAnImage(variable))
              .map((variableName) => {
                const labelText = t(`${variablegroup}.${variableName}`);
                return {
                  key: `${idx}.${variablegroup}.${variableName}`,
                  isRecursive: false,
                  label: buildVariableLabel(labelText, false),
                  name: labelText,
                  value: [opportunitydata, variablegroup, variableName],
                };
              });
          })
          .flat(),
      };
    });
  };
  const displayData = useMemo(() => {
    const rawData = formatingData(defaultVariablesApi, variablesDataRest);
    if (!rawData) return rawData;
    const filtered = isRecursiveTemplate && showOnlyRecursive
      ? rawData.filter((option) => option.isRecursive)
      : rawData;
    return [...filtered].sort((a, b) => {
      if (a.isRecursive !== b.isRecursive) return a.isRecursive ? -1 : 1;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }, [
    defaultVariablesApi,
    variablesDataRest,
    currentDocument.type,
    isRecursiveTemplate,
    recursiveModelName,
    recursiveModelFields,
    showOnlyRecursive,
    t,
  ]);

  const cascaderDisplayValue = useMemo(() => {
    if (!selectedBox) return undefined;
    const vp = selectedBox.variablePath;
    if (Array.isArray(vp) && vp.length > 0) return vp;
    const admin = selectedBox.content?.adminValue;
    if (!admin || !currentDocument || !defaultVariablesApi) return undefined;
    const path = buildImageCascaderPathFromAdmin(
      admin,
      currentDocument,
      defaultVariablesApi,
      variablesDataRest
    );
    return path ?? undefined;
  }, [selectedBox, currentDocument, defaultVariablesApi, variablesDataRest]);

  const sendingData = (e, selectedOptions) => {
    let selectedVariable;
    let adminValue;
    if (currentDocument.type === 'opportunity') {
      selectedVariable = e[0][e[1]][e[2]]?.toString();
      adminValue = `${[e[1]]}.${[e[2]]}`;
      if (!selectedVariable || selectedVariable === '') {
        selectedVariable = `#{${adminValue}}`;
      }
    } else if (currentDocument.type === 'template') {
      selectedVariable = e[0][e[1]]?.toString();
      adminValue = `${[e[0]]}.${[e[1]]}`;
      if (!selectedVariable || selectedVariable === '') {
        selectedVariable = `#{${adminValue}}`;
      }
    } else {
      let tab = e[e.length - 1];
      selectedVariable = tab[0][tab[1]][tab[2]]?.toString();
      if (e[0] === "offerVariables") {
        adminValue = `${[tab[1]]}.${[tab[2]]}`;
      } else {
        adminValue = `${selectedOptions[2].key.split(".")[0]}.${[tab[1]]}.${[tab[2]]}`;
      }
      if (!selectedVariable || selectedVariable === '') {
        selectedVariable = `#{${[tab[1]]}.${[tab[2]]}}`;
      }
    }
    onLoadImage(selectedVariable, adminValue);
  };
  return (
    <>
      {contextHolder}
      <CascaderCustom
        sytleCustom={{ width: '100%' }}
        tablesToDisplay={displayData}
        functionToExec={(e, selectedOptions) => {
          sendingData(e, selectedOptions);
          success();
        }}
        placement='bottomLeft'
        placeholderToDisplay='Variables Images'
        searchKey="name"
        selectedBox={selectedBox}
        displayValue={cascaderDisplayValue}
        dropdownRender={
          isRecursiveTemplate
            ? (menus) => (
              <div>
                <div
                  style={{
                    display: 'flex',
                    padding: '8px 12px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <Switch
                    checked={showOnlyRecursive}
                    onChange={setShowOnlyRecursive}
                    checkedChildren="Récursive"
                    unCheckedChildren="Toutes"
                    style={{ minWidth: 100 }}
                  />
                </div>
                {menus}
              </div>
            )
            : undefined
        }
        displayRender={(_labels, selectedOptions) => {
          const names = selectedOptions
            ?.map((o) => (typeof o?.name === 'string' ? o.name : null))
            .filter(Boolean);
          const isRecursive = selectedOptions?.some((o) => o?.isRecursive);
          let text = '';
          if (names?.length) {
            text = names.join(' / ');
          } else {
            const admin = selectedBox?.content?.adminValue;
            if (admin) {
              const parts = admin.split('.');
              if (isRecursiveTemplate && parts.length === 2 && parts[0] === recursiveModelName) {
                const field = recursiveModelFields?.[recursiveModelName]?.find((f) => f.key === parts[1]);
                text = field
                  ? buildRecursiveFieldName(recursiveModelName, field.label, t)
                  : admin;
              } else {
                const i18nKey =
                  parts.length >= 2
                    ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}`
                    : admin;
                text = t(i18nKey);
              }
            }
          }
          if (!text) return null;
          const showRecursiveIcon = isRecursive || (
            isRecursiveTemplate &&
            selectedBox?.content?.adminValue?.startsWith(`${recursiveModelName}.`)
          );
          return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {showRecursiveIcon ? <RecursiveFieldIcon /> : <ApiVariableIcon />}
              <span>{text}</span>
            </div>
          );
        }}
      />
    </>
  );
};
export default ImageCascader;
